import type { IEventRepository } from "@/core/repositories/interfaces/IEventRepository";
import type {
  CreateEventInput,
  CreateEventResult,
  Event,
  EventAdminListItem,
  EventAdminExportItem,
  EventEditor,
  EventLibraryTab,
  EventLibraryTabCounts,
  EventLibraryCategoryFilter,
  EventLibraryCategoryCounts,
  EventLibraryDateFilters,
  EventListPage,
  EventSummary,
  EventTaxonomy,
  EventLibrarySort,
  EventLibrarySortState,
  UpdateEventAssignmentInput,
  UpdateEventContentInput,
  UpdateEventInternalNotesInput,
  UpdateEventCoverImageInput,
  UpdateEventPlaceInput,
  UpdateEventStatusInput,
  UpdateEventTaxonomyInput,
} from "@/core/services/types/event.types";
import {
  EVENT_CATEGORY_VALUES,
  EVENT_CREATE_DEFAULT_TITLE,
  EMPTY_EVENT_LIBRARY_DATE_FILTERS,
  EVENT_LIBRARY_EXPORT_MAX_ROWS,
  EVENT_LIBRARY_TABS,
} from "@/core/services/types/event.types";
import {
  eventCalendarDayToExclusiveEndIso,
  eventCalendarDayToStartIso,
} from "@/lib/event-dates";
import type { LibraryPageSize } from "@/core/services/types/library-pagination.types";
import type { PlaceLinkedEvent } from "@/core/services/types/place.types";
import {
  editStatusToDbFields,
} from "@/core/services/types/article.types";
import { createClient } from "@/lib/supabase";
import type { TablesUpdate } from "@/types/database.types";

const EVENT_SUMMARY_COLUMNS =
  "id, title, description_short, cover_image_url, start_date, end_date, published_at, event_category" as const;

const EVENT_DETAIL_COLUMNS =
  `${EVENT_SUMMARY_COLUMNS}, description_long, pricing_info, place_id, whart_review_title, whart_review_content, whart_else` as const;

const EVENT_ADMIN_LIST_SELECT =
  "id, title, published_at, created_at, start_date, end_date, updated_at, status, event_category, assigned_to, cover_image_url, place_id, description_short, assignee:team_members!events_assigned_to_fkey(id, first_name, last_name), place:places(name, city)" as const;

const EVENT_ADMIN_EXPORT_SELECT =
  "id, title, description_short, start_date, end_date, department_name, place:places(name, city, zip_code, department_name)" as const;

const EVENT_EDITOR_COLUMNS =
  "id, title, description_short, description_long, cover_image_url, status, published_at, assigned_to, updated_at, start_date, end_date, place_id, event_category, pricing_info, whart_review_title, whart_review_content, whart_else, activity_type, is_family, is_recurrent, activity_schedule, internal_notes" as const;

type EventAdminListRow = {
  id: string;
  title: string;
  published_at: string | null;
  created_at: string;
  start_date: string;
  end_date: string;
  updated_at: string;
  status: EventAdminListItem["status"];
  event_category: EventAdminListItem["event_category"];
  assigned_to: string | null;
  cover_image_url: string | null;
  place_id: string | null;
  description_short: string | null;
  assignee: EventAdminListItem["assignee"] | EventAdminListItem["assignee"][];
  place: EventAdminListItem["place"] | EventAdminListItem["place"][];
};

function normalizeEventAdminListRow(row: EventAdminListRow): EventAdminListItem {
  const assignee = Array.isArray(row.assignee) ? (row.assignee[0] ?? null) : row.assignee;
  const place = Array.isArray(row.place) ? (row.place[0] ?? null) : row.place;

  return {
    id: row.id,
    title: row.title,
    published_at: row.published_at,
    created_at: row.created_at,
    start_date: row.start_date,
    end_date: row.end_date,
    updated_at: row.updated_at,
    status: row.status,
    event_category: row.event_category,
    assigned_to: row.assigned_to,
    cover_image_url: row.cover_image_url,
    place_id: row.place_id,
    description_short: row.description_short,
    assignee,
    place,
  };
}

type EventAdminExportRow = {
  id: string;
  title: string;
  description_short: string | null;
  start_date: string;
  end_date: string;
  department_name: string | null;
  place:
    | EventAdminExportItem["place"]
    | EventAdminExportItem["place"][]
    | null;
};

function normalizeEventAdminExportRow(row: EventAdminExportRow): EventAdminExportItem {
  const place = Array.isArray(row.place) ? (row.place[0] ?? null) : row.place;

  return {
    id: row.id,
    title: row.title,
    description_short: row.description_short,
    start_date: row.start_date,
    end_date: row.end_date,
    department_name: row.department_name,
    place,
  };
}

function escapeIlikePattern(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/%/g, "\\%")
    .replace(/_/g, "\\_");
}

function buildTitleDescriptionSearchFilter(search: string): string {
  const pattern = `%${escapeIlikePattern(search)}%`;
  return `title.ilike."${pattern}",description_short.ilike."${pattern}"`;
}

function applySearchFilter<T extends { or: (filters: string) => T }>(
  query: T,
  search: string,
): T {
  if (!search) return query;
  return query.or(buildTitleDescriptionSearchFilter(search));
}

function applyCategoryFilter<T extends { eq: (column: string, value: string) => T }>(
  query: T,
  category: EventLibraryCategoryFilter,
): T {
  if (category === "ALL") return query;
  return query.eq("event_category", category);
}

function applyDateFilters<T extends {
  gte: (column: string, value: string) => T;
  lt: (column: string, value: string) => T;
}>(
  query: T,
  dateFilters: EventLibraryDateFilters,
): T {
  let next = query;

  if (dateFilters.startFrom) {
    const parsed = eventCalendarDayToStartIso(dateFilters.startFrom);
    if (parsed) {
      next = next.gte("start_date", parsed);
    }
  }

  if (dateFilters.startTo) {
    const parsed = eventCalendarDayToExclusiveEndIso(dateFilters.startTo);
    if (parsed) {
      next = next.lt("start_date", parsed);
    }
  }

  if (dateFilters.endFrom) {
    const parsed = eventCalendarDayToStartIso(dateFilters.endFrom);
    if (parsed) {
      next = next.gte("end_date", parsed);
    }
  }

  if (dateFilters.endTo) {
    const parsed = eventCalendarDayToExclusiveEndIso(dateFilters.endTo);
    if (parsed) {
      next = next.lt("end_date", parsed);
    }
  }

  return next;
}

function applyLibraryTabFilters<
  T extends {
    or: (filters: string) => T;
    eq: (column: string, value: string) => T;
    gte: (column: string, value: string) => T;
    lte: (column: string, value: string) => T;
    gt: (column: string, value: string) => T;
    lt: (column: string, value: string) => T;
    not: (column: string, operator: string, value: string) => T;
  },
>(
  query: T,
  tab: EventLibraryTab,
  search = "",
  category: EventLibraryCategoryFilter = "ALL",
  dateFilters: EventLibraryDateFilters = EMPTY_EVENT_LIBRARY_DATE_FILTERS,
): T {
  let next = applySearchFilter(query, search);
  next = applyCategoryFilter(next, category);
  next = applyDateFilters(next, dateFilters);

  if (tab === "PUBLISHED") {
    return next.eq("status", "PUBLISHED");
  }

  if (tab !== "ALL") {
    return next.eq("status", tab);
  }

  return next;
}

type OrderableQuery = {
  order: (
    column: string,
    options?: {
      ascending?: boolean;
      foreignTable?: string;
      nullsFirst?: boolean;
    },
  ) => OrderableQuery;
};

function applyEventLibrarySortColumn(
  query: OrderableQuery,
  sort: EventLibrarySort,
): OrderableQuery {
  const ascending = sort.direction === "asc";

  switch (sort.column) {
    case "title":
      return query.order("title", { ascending });
    case "event_category":
      return query.order("event_category", { ascending, nullsFirst: true });
    case "place":
      return query.order("name", {
        ascending,
        foreignTable: "places",
        nullsFirst: true,
      });
    case "assignment":
      return query.order("last_name", {
        ascending,
        foreignTable: "team_members",
        nullsFirst: true,
      });
    case "start_date":
      return query.order("start_date", { ascending });
    case "end_date":
      return query.order("end_date", { ascending });
    case "created_at":
      return query.order("created_at", { ascending });
    case "updated_at":
      return query.order("updated_at", { ascending });
    case "published_at":
      return query.order("published_at", {
        ascending,
        nullsFirst: ascending,
      });
    case "status":
      return query.order("status", { ascending });
  }
}

function applyEventLibraryOrder(
  query: OrderableQuery,
  tab: EventLibraryTab,
  sort: EventLibrarySortState,
): OrderableQuery {
  if (sort) {
    return applyEventLibrarySortColumn(query, sort);
  }

  if (tab === "ALL") {
    return query.order("updated_at", { ascending: false });
  }

  if (tab === "PUBLISHED") {
    return query.order("published_at", { ascending: false });
  }

  return query.order("updated_at", { ascending: false });
}

function buildLibraryListTabQuery(
  supabase: Awaited<ReturnType<typeof createClient>>,
  tab: EventLibraryTab,
  search = "",
  category: EventLibraryCategoryFilter = "ALL",
  dateFilters: EventLibraryDateFilters = EMPTY_EVENT_LIBRARY_DATE_FILTERS,
  sort: EventLibrarySortState = null,
  options?: { count?: "exact" },
) {
  const query = supabase
    .from("events")
    .select(
      EVENT_ADMIN_LIST_SELECT,
      options?.count ? { count: "exact" } : undefined,
    );

  let next = applyLibraryTabFilters(query, tab, search, category, dateFilters);
  next = applyEventLibraryOrder(next, tab, sort) as typeof next;
  return next;
}

function buildLibraryExportTabQuery(
  supabase: Awaited<ReturnType<typeof createClient>>,
  tab: EventLibraryTab,
  search = "",
  category: EventLibraryCategoryFilter = "ALL",
  dateFilters: EventLibraryDateFilters = EMPTY_EVENT_LIBRARY_DATE_FILTERS,
) {
  const query = supabase.from("events").select(EVENT_ADMIN_EXPORT_SELECT);
  let next = applyLibraryTabFilters(query, tab, search, category, dateFilters);
  next = applyEventLibraryOrder(next, tab, null) as typeof next;
  return next;
}

function emptyLibraryCategoryCounts(): EventLibraryCategoryCounts {
  return {
    ALL: 0,
    PERMANENT_COLLECTION: 0,
    TEMPORARY_COLLECTION: 0,
    ACTIVITY: 0,
  };
}

function emptyLibraryTabCounts(): EventLibraryTabCounts {
  return {
    ALL: 0,
    DRAFT: 0,
    PENDING_REVIEW: 0,
    PUBLISHED: 0,
    ARCHIVED: 0,
  };
}

type EventTaxonomyRow = {
  taxonomy_id: string;
  taxonomy: { category: string } | { category: string }[] | null;
};

function normalizeTaxonomyRelation(
  value: EventTaxonomyRow["taxonomy"],
): { category: string } | null {
  if (!value) return null;
  return Array.isArray(value) ? (value[0] ?? null) : value;
}

function getCurrentMonthUtcRange(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));

  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
}

async function fetchPlaceDepartmentName(
  supabase: Awaited<ReturnType<typeof createClient>>,
  placeId: string,
): Promise<string | null> {
  const { data, error } = await supabase
    .from("places")
    .select("department_name")
    .eq("id", placeId)
    .maybeSingle();

  if (error) {
    console.error("[EventRepository.fetchPlaceDepartmentName]", error.message);
    return null;
  }

  return data?.department_name ?? null;
}

export class EventRepository implements IEventRepository {
  async createAdmin(input: CreateEventInput): Promise<CreateEventResult> {
    const supabase = await createClient();
    const title = input.title?.trim() || EVENT_CREATE_DEFAULT_TITLE;
    const placeId = input.placeId?.trim() || null;
    const departmentName = placeId
      ? await fetchPlaceDepartmentName(supabase, placeId)
      : null;

    const { data, error } = await supabase
      .from("events")
      .insert({
        title,
        place_id: placeId,
        department_name: departmentName,
        ...(input.startDate ? { start_date: input.startDate } : {}),
        ...(input.endDate ? { end_date: input.endDate } : {}),
        ...(input.assignedTo ? { assigned_to: input.assignedTo } : {}),
        status: input.status ?? "DRAFT",
        source_type: "MANUAL",
        publish_on_web: false,
        publish_on_mobile: false,
      })
      .select(EVENT_EDITOR_COLUMNS)
      .single();

    if (error) {
      console.error("[EventRepository.createAdmin]", error.message);
      return { success: false, error: error.message };
    }

    if (!data) {
      return { success: false, error: "Event insert returned no data" };
    }

    return { success: true, data };
  }

  async deleteAdmin(id: string): Promise<boolean> {
    const supabase = await createClient();

    const { error } = await supabase.from("events").delete().eq("id", id);

    if (error) {
      console.error("[EventRepository.deleteAdmin]", error.message);
      return false;
    }

    return true;
  }

  async findPublished(limit = 10): Promise<EventSummary[]> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("events")
      .select(EVENT_SUMMARY_COLUMNS)
      .eq("status", "PUBLISHED")
      .eq("publish_on_web", true)
      .order("start_date", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("[EventRepository.findPublished]", error.message);
      return [];
    }

    return data ?? [];
  }

  async findById(id: string): Promise<Event | null> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("events")
      .select(EVENT_DETAIL_COLUMNS)
      .eq("id", id)
      .eq("status", "PUBLISHED")
      .eq("publish_on_web", true)
      .maybeSingle();

    if (error) {
      console.error("[EventRepository.findById]", error.message);
      return null;
    }

    return data;
  }

  async findAdminById(id: string): Promise<EventEditor | null> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("events")
      .select(EVENT_EDITOR_COLUMNS)
      .eq("id", id)
      .maybeSingle();

    if (error) {
      console.error("[EventRepository.findAdminById]", error.message);
      return null;
    }

    return data;
  }

  async updateAdminContent(
    id: string,
    input: UpdateEventContentInput,
  ): Promise<EventEditor | null> {
    const supabase = await createClient();

    const updatePayload: TablesUpdate<"events"> = {
      title: input.title,
      description_short: input.description_short,
      description_long: input.description_long,
      whart_review_title: input.whart_review_title,
      whart_review_content: input.whart_review_content,
      whart_else: input.whart_else,
      event_category: input.event_category,
      pricing_info: input.pricing_info,
      updated_at: new Date().toISOString(),
    };

    if (input.event_category === "ACTIVITY") {
      updatePayload.activity_type = input.activity_type;
      updatePayload.is_family = input.is_family;
      updatePayload.is_recurrent = input.is_recurrent;
      updatePayload.activity_schedule = input.activity_schedule;

      if (input.start_date !== undefined) {
        updatePayload.start_date = input.start_date;
      }
      if (input.end_date !== undefined) {
        updatePayload.end_date = input.end_date;
      }
    } else {
      updatePayload.activity_type = null;
      updatePayload.is_family = false;
      updatePayload.is_recurrent = false;
      updatePayload.activity_schedule = null;

      if (input.event_category !== "PERMANENT_COLLECTION") {
        if (input.start_date !== undefined) {
          updatePayload.start_date = input.start_date;
        }
        if (input.end_date !== undefined) {
          updatePayload.end_date = input.end_date;
        }
      }
    }

    const { data, error } = await supabase
      .from("events")
      .update(updatePayload)
      .eq("id", id)
      .select(EVENT_EDITOR_COLUMNS)
      .maybeSingle();

    if (error) {
      console.error("[EventRepository.updateAdminContent]", error.message);
      return null;
    }

    return data;
  }

  async updateAdminStatus(
    id: string,
    input: UpdateEventStatusInput,
  ): Promise<EventEditor | null> {
    const supabase = await createClient();
    const existing = await this.findAdminById(id);

    if (!existing) {
      return null;
    }

    const statusFields = editStatusToDbFields(
      input.editStatus,
      existing.published_at,
    );

    const { data, error } = await supabase
      .from("events")
      .update({
        status: statusFields.status,
        published_at: statusFields.published_at,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select(EVENT_EDITOR_COLUMNS)
      .maybeSingle();

    if (error) {
      console.error("[EventRepository.updateAdminStatus]", error.message);
      return null;
    }

    return data;
  }

  async updateAdminAssignment(
    id: string,
    input: UpdateEventAssignmentInput,
  ): Promise<EventEditor | null> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("events")
      .update({
        assigned_to: input.assignedTo,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select(EVENT_EDITOR_COLUMNS)
      .maybeSingle();

    if (error) {
      console.error("[EventRepository.updateAdminAssignment]", error.message);
      return null;
    }

    return data;
  }

  async updateAdminInternalNotes(
    id: string,
    input: UpdateEventInternalNotesInput,
  ): Promise<EventEditor | null> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("events")
      .update({
        internal_notes: input.internalNotes,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select(EVENT_EDITOR_COLUMNS)
      .maybeSingle();

    if (error) {
      console.error("[EventRepository.updateAdminInternalNotes]", error.message);
      return null;
    }

    return data;
  }

  async updateAdminCoverImage(
    id: string,
    input: UpdateEventCoverImageInput,
  ): Promise<EventEditor | null> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("events")
      .update({
        cover_image_url: input.coverImageUrl,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select(EVENT_EDITOR_COLUMNS)
      .maybeSingle();

    if (error) {
      console.error("[EventRepository.updateAdminCoverImage]", error.message);
      return null;
    }

    return data;
  }

  async updateAdminPlace(
    id: string,
    input: UpdateEventPlaceInput,
  ): Promise<EventEditor | null> {
    const supabase = await createClient();
    const departmentName = await fetchPlaceDepartmentName(supabase, input.placeId);

    const { data, error } = await supabase
      .from("events")
      .update({
        place_id: input.placeId,
        department_name: departmentName,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select(EVENT_EDITOR_COLUMNS)
      .maybeSingle();

    if (error) {
      console.error("[EventRepository.updateAdminPlace]", error.message);
      return null;
    }

    return data;
  }

  async findByLibraryTab(
    tab: EventLibraryTab,
    page: number,
    pageSize: LibraryPageSize,
    search = "",
    category: EventLibraryCategoryFilter = "ALL",
    dateFilters: EventLibraryDateFilters = EMPTY_EVENT_LIBRARY_DATE_FILTERS,
    sort: EventLibrarySortState = null,
  ): Promise<EventListPage> {
    const supabase = await createClient();
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const query = buildLibraryListTabQuery(
      supabase,
      tab,
      search,
      category,
      dateFilters,
      sort,
      { count: "exact" },
    );

    const { data, error, count } = await query.range(from, to);

    if (error) {
      console.error("[EventRepository.findByLibraryTab]", error.message);
      return { items: [], total: 0, page, pageSize };
    }

    return {
      items: (data ?? []).map((row) =>
        normalizeEventAdminListRow(row as EventAdminListRow),
      ),
      total: count ?? 0,
      page,
      pageSize,
    };
  }

  async findAllByLibraryTab(
    tab: EventLibraryTab,
    search = "",
    category: EventLibraryCategoryFilter = "ALL",
    dateFilters: EventLibraryDateFilters = EMPTY_EVENT_LIBRARY_DATE_FILTERS,
  ): Promise<EventAdminExportItem[]> {
    const supabase = await createClient();

    const { data, error } = await buildLibraryExportTabQuery(
      supabase,
      tab,
      search,
      category,
      dateFilters,
    ).range(0, EVENT_LIBRARY_EXPORT_MAX_ROWS - 1);

    if (error) {
      console.error("[EventRepository.findAllByLibraryTab]", error.message);
      return [];
    }

    return (data ?? []).map((row) =>
      normalizeEventAdminExportRow(row as EventAdminExportRow),
    );
  }

  async countByLibraryTab(
    search = "",
    category: EventLibraryCategoryFilter = "ALL",
    dateFilters: EventLibraryDateFilters = EMPTY_EVENT_LIBRARY_DATE_FILTERS,
  ): Promise<EventLibraryTabCounts> {
    const supabase = await createClient();
    const counts = emptyLibraryTabCounts();

    function baseCountQuery() {
      let query = supabase
        .from("events")
        .select("id", { count: "exact", head: true });

      query = applySearchFilter(query, search);
      query = applyCategoryFilter(query, category);
      query = applyDateFilters(query, dateFilters);

      return query;
    }

    await Promise.all([
      (async () => {
        const { count, error } = await baseCountQuery();

        if (error) {
          console.error("[EventRepository.countByLibraryTab] ALL", error.message);
          return;
        }

        counts.ALL = count ?? 0;
      })(),
      ...EVENT_LIBRARY_TABS.filter((tab) => tab !== "ALL").map(async (status) => {
        const { count, error } = await baseCountQuery().eq("status", status);

        if (error) {
          console.error(
            `[EventRepository.countByLibraryTab] ${status}`,
            error.message,
          );
          return;
        }

        counts[status] = count ?? 0;
      }),
    ]);

    return counts;
  }

  async countByLibraryCategory(): Promise<EventLibraryCategoryCounts> {
    const supabase = await createClient();
    const counts = emptyLibraryCategoryCounts();

    await Promise.all([
      (async () => {
        const { count, error } = await supabase
          .from("events")
          .select("id", { count: "exact", head: true });

        if (error) {
          console.error("[EventRepository.countByLibraryCategory] ALL", error.message);
          return;
        }

        counts.ALL = count ?? 0;
      })(),
      ...EVENT_CATEGORY_VALUES.map(async (category) => {
        const { count, error } = await supabase
          .from("events")
          .select("id", { count: "exact", head: true })
          .eq("event_category", category);

        if (error) {
          console.error(
            `[EventRepository.countByLibraryCategory] ${category}`,
            error.message,
          );
          return;
        }

        counts[category] = count ?? 0;
      }),
    ]);

    return counts;
  }

  async countPublishedThisMonth(): Promise<number> {
    const supabase = await createClient();
    const { start, end } = getCurrentMonthUtcRange();
    const now = new Date().toISOString();

    const { count, error } = await supabase
      .from("events")
      .select("id", { count: "exact", head: true })
      .gte("published_at", start)
      .lt("published_at", end)
      .lte("published_at", now);

    if (error) {
      console.error("[EventRepository.countPublishedThisMonth]", error.message);
      return 0;
    }

    return count ?? 0;
  }

  async findAdminByPlaceId(placeId: string): Promise<PlaceLinkedEvent[]> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("events")
      .select("id, title, start_date, end_date, status, event_category")
      .eq("place_id", placeId)
      .order("start_date", { ascending: false });

    if (error) {
      console.error("[EventRepository.findAdminByPlaceId]", error.message);
      return [];
    }

    return data ?? [];
  }

  async resyncLinkedEventsForPlace(
    placeId: string,
    departmentName: string | null,
  ): Promise<number> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("events")
      .update({
        department_name: departmentName,
        updated_at: new Date().toISOString(),
      })
      .eq("place_id", placeId)
      .select("id");

    if (error) {
      console.error(
        "[EventRepository.resyncLinkedEventsForPlace]",
        error.message,
      );
      return 0;
    }

    return data?.length ?? 0;
  }

  async findTaxonomy(eventId: string): Promise<EventTaxonomy> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("event_taxonomy")
      .select("taxonomy_id, taxonomy:taxonomy_id(category)")
      .eq("event_id", eventId);

    if (error) {
      console.error("[EventRepository.findTaxonomy]", error.message);
      return {
        artFormIds: [],
        artisticPeriodIds: [],
        culturalAreaIds: [],
        exhibitionTypeIds: [],
      };
    }

    const artFormIds: string[] = [];
    const artisticPeriodIds: string[] = [];
    const culturalAreaIds: string[] = [];
    const exhibitionTypeIds: string[] = [];

    for (const row of data ?? []) {
      const taxonomy = normalizeTaxonomyRelation(row.taxonomy);
      if (!taxonomy) continue;

      if (taxonomy.category === "ART_FORM") {
        artFormIds.push(row.taxonomy_id);
      } else if (taxonomy.category === "ARTISTIC_PERIOD") {
        artisticPeriodIds.push(row.taxonomy_id);
      } else if (taxonomy.category === "CULTURAL_AREA") {
        culturalAreaIds.push(row.taxonomy_id);
      } else if (taxonomy.category === "EXHIBITION_TYPE") {
        exhibitionTypeIds.push(row.taxonomy_id);
      }
    }

    return { artFormIds, artisticPeriodIds, culturalAreaIds, exhibitionTypeIds };
  }

  async replaceTaxonomy(
    eventId: string,
    input: UpdateEventTaxonomyInput,
  ): Promise<boolean> {
    const supabase = await createClient();

    const { error } = await supabase.rpc("replace_content_taxonomy", {
      p_entity_type: "event",
      p_entity_id: eventId,
      p_taxonomy_ids: [
        ...input.artFormIds,
        ...input.artisticPeriodIds,
        ...input.culturalAreaIds,
        ...input.exhibitionTypeIds,
      ],
    });

    if (error) {
      console.error("[EventRepository.replaceTaxonomy]", error.message);
      return false;
    }
    return true;
  }
}
